import React, { useState } from 'react';
import { buildShareLinks } from '../../services/roomService';
import { useEmbeddedDialog } from '../../utils/embedDialog';
import './ScheduledRoomLinkModal.css';

function formatRange(start, end) {
  if (!start) return '';
  const startDate = new Date(start);
  const endDate = end ? new Date(end) : null;
  const dayOpts = { weekday: 'short', month: 'short', day: 'numeric' };
  const timeOpts = { hour: 'numeric', minute: '2-digit' };
  const day = startDate.toLocaleDateString(undefined, dayOpts);
  const startTime = startDate.toLocaleTimeString(undefined, timeOpts);
  if (!endDate || Number.isNaN(endDate.getTime())) return `${day} · ${startTime}`;
  const endTime = endDate.toLocaleTimeString(undefined, timeOpts);
  return `${day} · ${startTime} – ${endTime}`;
}

export default function ScheduledRoomLinkModal({
  isOpen,
  onClose,
  roomName,
  title,
  scheduledStartTime,
  scheduledEndTime,
  hostLink,
  teamLink,
  candidateLink,
  sageJoin,
  participants = [],
  inviteSummary = null,
}) {
  const [copied, setCopied] = useState('');
  useEmbeddedDialog(isOpen);
  const local = roomName
    ? buildShareLinks(roomName)
    : { hostLink: '', teamLink: '', candidateLink: '' };
  const hostUrl = hostLink || local.hostLink;
  const teammateUrl = teamLink || local.teamLink;
  const candidateUrl = candidateLink || local.candidateLink;

  const copyToClipboard = async (link, key) => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(key);
      window.setTimeout(() => setCopied((current) => (current === key ? '' : current)), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (!isOpen) return null;

  const invitedCount = Array.isArray(participants) ? participants.length : 0;
  const sentCount = inviteSummary?.sent || 0;
  const skippedCount = inviteSummary?.skipped || 0;
  const failedCount = inviteSummary?.failed || 0;

  return (
    <div className="mg-link-overlay" onClick={onClose}>
      <div className="mg-link-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mg-link-head">
          <div className="mg-link-badge" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M9 12l2 2 4-4"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
            </svg>
          </div>
          <div>
            <h2>Meeting scheduled</h2>
            {title ? <p className="mg-link-title">{title}</p> : null}
            {scheduledStartTime ? (
              <p className="mg-link-when">{formatRange(scheduledStartTime, scheduledEndTime)}</p>
            ) : null}
          </div>
        </div>

        {sageJoin ? (
          <div className="mg-link-sage">Sage will join automatically when a host enters.</div>
        ) : null}

        {invitedCount > 0 ? (
          <div className="mg-link-invites">
            {sentCount > 0
              ? `Invite email sent to ${sentCount} participant${sentCount === 1 ? '' : 's'} (team link).`
              : skippedCount > 0
                ? 'Participants saved. Email is not configured — share links manually.'
                : failedCount > 0
                  ? 'Meeting saved, but some invite emails failed. Share links below.'
                  : `Participants saved (${invitedCount}).`}
          </div>
        ) : null}

        <div className="mg-link-block">
          <div className="mg-link-label-row">
            <span className="mg-link-label">Your host link</span>
            <span className="mg-link-chip is-host">/host</span>
          </div>
          <div className="mg-link-row">
            <input readOnly value={hostUrl} />
            <button
              type="button"
              className={`mg-link-copy is-host${copied === 'host' ? ' is-copied' : ''}`}
              onClick={() => copyToClipboard(hostUrl, 'host')}
            >
              {copied === 'host' ? 'Copied' : 'Copy host link'}
            </button>
          </div>
        </div>

        <div className="mg-link-block">
          <div className="mg-link-label-row">
            <span className="mg-link-label">Teammate link</span>
            <span className="mg-link-chip is-team">/team</span>
          </div>
          <div className="mg-link-row">
            <input readOnly value={teammateUrl} />
            <button
              type="button"
              className={`mg-link-copy is-team${copied === 'team' ? ' is-copied' : ''}`}
              onClick={() => copyToClipboard(teammateUrl, 'team')}
            >
              {copied === 'team' ? 'Copied' : 'Copy teammate link'}
            </button>
          </div>
        </div>

        <div className="mg-link-block">
          <div className="mg-link-label-row">
            <span className="mg-link-label">Candidate link</span>
            <span className="mg-link-chip is-candidate">/candidate</span>
          </div>
          <div className="mg-link-row">
            <input readOnly value={candidateUrl} disabled={!candidateUrl} />
            <button
              type="button"
              className={`mg-link-copy is-candidate${copied === 'candidate' ? ' is-copied' : ''}`}
              onClick={() => copyToClipboard(candidateUrl, 'candidate')}
              disabled={!candidateUrl}
            >
              {copied === 'candidate' ? 'Copied' : 'Copy candidate link'}
            </button>
          </div>
        </div>

        <div className="mg-link-actions">
          <button type="button" className="mg-link-done" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
